import React, { useState } from "react";
import { mockData } from "../data/mockData";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "./ui/button";

const TestimonialsSection = ({ data }) => {
  const testimonials = data?.testimonials || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  
  const testimonialsToShow = showAll ? testimonials : testimonials.slice(0, 6);
  const featuredTestimonial = testimonials.length > 0 ? testimonials[currentIndex] : null;

  const nextTestimonial = () => {
    if (testimonials.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }
  };

  const prevTestimonial = () => {
    if (testimonials.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  if (!featuredTestimonial) {
    return (
      <section id="testimonials" className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-white/60">Loading testimonials...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="py-24 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <p className="text-sm text-red-500 mb-4 tracking-wider uppercase">RESIDENT REVIEWS</p>
            <h2 className="text-4xl md:text-5xl font-light mb-8">Real Stories, Real Results</h2>
            <div className="w-24 h-px bg-red-500 mx-auto mb-8"></div>
            <p className="text-xl text-white/80 max-w-3xl mx-auto font-light">
              Hear from the residents whose lives have been transformed through our specialised healthcare programmes
            </p>
          </div>

          {/* Featured Testimonial */}
          <div className="mb-20">
            <Card className="bg-black/40 border-white/10 hover:border-red-500/30 transition-all duration-500 max-w-4xl mx-auto">
              <CardContent className="p-12 text-center">
                <Quote className="w-12 h-12 text-red-500 mx-auto mb-8 opacity-60" />
                <p className="text-2xl md:text-3xl text-white/90 italic leading-relaxed font-light mb-8">
                  "{featuredTestimonial.quote}"
                </p>
                <div className="mb-6">
                  <div className="flex justify-center text-red-500 mb-4">
                    {[...Array(featuredTestimonial.rating)].map((_, i) => (
                      <span key={i} className="text-xl">★</span>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-white font-light text-lg">{featuredTestimonial.name}{featuredTestimonial.age ? `, ${featuredTestimonial.age}` : ''}</p>
                  <Badge variant="outline" className="border-red-500/30 text-red-500/80 mt-4">
                    {featuredTestimonial.programme}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Controls */}
            <div className="flex justify-center items-center space-x-4 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={prevTestimonial}
                className="border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex ? 'bg-red-500' : 'bg-white/30 hover:bg-white/50'
                    }`}
                  />
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={nextTestimonial}
                className="border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Testimonials Grid */}
          <div className="mb-12">
            <h3 className="text-2xl font-light text-center mb-12">More Success Stories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonialsToShow.map((testimonial) => (
                <Card 
                  key={testimonial.id}
                  className="bg-gray-800/50 border-white/10 hover:border-red-500/30 transition-all duration-300 hover:scale-105 group cursor-pointer"
                  onClick={() => setCurrentIndex(testimonial.id - 1)}
                >
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="flex text-red-500 mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <span key={i} className="text-sm">★</span>
                        ))}
                      </div>
                      <Badge variant="outline" className="border-orange-500/30 text-orange-500/80 text-xs mb-4">
                        {testimonial.programme}
                      </Badge>
                      <p className="text-white/80 italic leading-relaxed font-light text-sm mb-4 line-clamp-3">
                        "{testimonial.quote}"
                      </p>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-white font-light text-sm">{testimonial.name}{testimonial.age ? `, ${testimonial.age}` : ''}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Show More/Less Button */}
          <div className="text-center">
            <Button
              onClick={() => setShowAll(!showAll)}
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300"
            >
              {showAll ? 'Show Less' : `View All ${testimonials.length} Reviews`}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;